import React from 'react'
import { Grid, Box, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { PAYMENT_INFORMATION_DESCRIPTION } from '../../../utils/payments'
import { toLocalizedNumber } from '../../../utils/helpers'

interface DetailProps {
  label: string
  value: string
  isEmphasized?: boolean
  isLast?: boolean
}

const Detail = (props: DetailProps) => {
  const theme = useTheme()
  const valueStyle = props.isEmphasized
    ? {
        fontSize: '3rem',
        fontWeight: 700,
        lineHeight: '3rem',
      }
    : {
        fontSize: '1.25rem',
        fontWeight: 400,
        lineHeight: '1rem',
      }
  const labelStyle = props.isEmphasized
    ? {
        fontSize: '0.875rem',
        fontWeight: 600,
        color: '#00000080',
      }
    : {
        fontSize: '0.75rem',
        fontWeight: 400,
        color: '#0000004D',
      }
  return (
    <Box
      sx={theme => ({
        marginBottom: props.isLast ? 0 : theme.spacing(2.5),
      })}
    >
      <Typography
        sx={{
          textAlign: 'center',
          color: '#000000B2',
          marginBottom: theme.spacing(1),
          ...valueStyle,
        }}
      >
        {props.value}
      </Typography>
      <Typography
        sx={{
          lineHeight: '1rem',
          textAlign: 'center',
          ...labelStyle,
        }}
      >
        {props.label}
      </Typography>
    </Box>
  )
}

interface DetailSectionProps {
  details: Array<DetailProps>
  hasDivider?: boolean
  hasBackground?: boolean
}

const DetailSection = (props: DetailSectionProps) => (
  <Box
    sx={theme => ({
      padding: theme.spacing(2.5, 0),
      ...(props.hasDivider && {
        borderBottom: '1px solid #00000033',
      }),
      ...(props.hasBackground && {
        backgroundColor: '#00000003',
      }),
    })}
  >
    {props.details.map((detail, index) => (
      <Detail
        key={detail.label}
        label={detail.label}
        value={detail.value}
        isEmphasized={detail.isEmphasized}
        isLast={index === props.details.length - 1}
      />
    ))}
  </Box>
)

interface PaymentDetailsReadOnlyProps {
  accountName: string
  amount: string
  accountNumber: string
  memo?: string
}

const PaymentDetailsReadOnly = (props: PaymentDetailsReadOnlyProps) => {
  const amountSectionDetails = [
    {
      label: 'Payment Amount',
      value: toLocalizedNumber(parseFloat(props.amount), 'USD'),
      isEmphasized: true,
    },
  ]
  const metaDataSectionDetails = [
    {
      label: PAYMENT_INFORMATION_DESCRIPTION.ACCOUNT_NAME,
      value: props.accountName,
    },
    {
      label: PAYMENT_INFORMATION_DESCRIPTION.ACCOUNT_NUMBER,
      value: props.accountNumber,
    },
    ...(props.memo
      ? [{ label: PAYMENT_INFORMATION_DESCRIPTION.MEMO, value: props.memo }]
      : []),
  ]
  return (
    <Grid
      sx={theme => ({
        border: '1px solid #00000033',
        borderRadius: '8px',
        boxShadow: '0px 2px 4px 0px #00000012',
        marginBottom: theme.spacing(3),
      })}
    >
      <DetailSection details={amountSectionDetails} hasDivider />
      <DetailSection details={metaDataSectionDetails} hasBackground />
    </Grid>
  )
}

export default PaymentDetailsReadOnly
